<template>
  <div class="flex flex-col">
    <LabeledText :label="t('rentalDuration')"
      ><p class="py-1">
        {{ booking.totalRentalDays }} {{ t('days') }}
        <span v-if="booking.totalRentalDays !== booking.priceCalculation.days">{{
          t('priceCalculatedForXDays', { days: booking.priceCalculation.days })
        }}</span>
      </p></LabeledText
    >
    <div v-if="showOverwriteInclusiveKm && !immutable" class="mb-2 mt-2">
      <div class="flex items-center">
        <div class="w-32">
          <InputField name="customInclusiveKm" type="number" :min="0" adornment="km" />
        </div>
        <div class="ml-2">
          <div>+ {{ currentInclusiveAndExtraKm.extraKm }} {{ t('extraKm') }}</div>
          <div v-if="currentInclusiveAndExtraKm.couponKm > 0">
            + {{ currentInclusiveAndExtraKm.couponKm }} {{ t('couponKm') }}
          </div>
        </div>
      </div>
    </div>
    <Information
      ><template #text
        >{{ showOverwriteInclusiveKm && !immutable ? '=' : '' }}
        {{ currentInclusiveAndExtraKm.sum + ' ' + t('inclusiveKm') }}</template
      ><template #modal>{{ t('information') }}</template></Information
    >
    <p v-if="!immutable && !hasChanged">
      <a class="text-link" href="#" @click.prevent="toggleOverwriteInclusiveKm">{{
        showOverwriteInclusiveKm ? t('useInclusiveKmOfCar') : t('overwriteInclusiveKm')
      }}</a>
    </p>
    <div v-if="hasChanged" class="mt-2 flex justify-end gap-2">
      <CVButton variant="default" :is-loading="isBusy" @click.prevent="onSubmit">
        {{ t('save') }}
      </CVButton>
      <CVButton :disabled="isBusy" @click.prevent="onCancel">
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import Information from '../Information.vue';
import InputField from '../InputField.vue';
import LabeledText from '../LabeledText.vue';
import * as yup from 'yup';
import type { Booking } from '@/entities/bookings/booking.entity';
import { cloneDeep } from 'lodash';
import { useFormHasChanged } from '@/hooks/use-form-has-changed';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';
import CVButton from '../buttons/CVButton.vue';
import { PriceRecalculationMode } from '@carvia/ros-client-types';
import { getInclusiveAndExtraKm } from '@/utils/get-inclusive-and-extra-km';

const props = defineProps<{
  booking: Booking;
  immutable?: boolean;
}>();

const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
}>();

const { t } = useI18n();

const { confirmAndSave, isBusy } = usePriceChangeConfirmation();
const toggleOverwriteInclusiveKm = () => {
  showOverwriteInclusiveKm.value = !showOverwriteInclusiveKm.value;
  if (showOverwriteInclusiveKm.value) {
    setFieldValue('customInclusiveKm', props.booking.priceCalculation.inclusiveKm);
  } else {
    setFieldValue('customInclusiveKm', null);
  }
};

const currentInclusiveKm = computed(
  () => formValues.customInclusiveKm ?? props.booking.priceCalculation.defaultInclusiveKm,
);
const currentInclusiveAndExtraKm = computed(() => {
  return getInclusiveAndExtraKm(props.booking, currentInclusiveKm.value);
});

const onCancel = () => {
  setFieldValue('customInclusiveKm', props.booking.priceCalculation.customInclusiveKm);
  showOverwriteInclusiveKm.value = !!props.booking.priceCalculation.customInclusiveKm;
};

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
} = useForm({
  initialValues: {
    customInclusiveKm: props.booking.priceCalculation.customInclusiveKm,
  },
  validationSchema: yup.object({
    customInclusiveKm: yup.number().required().nullable(),
  }),
  keepValuesOnUnmount: true,
});

const showOverwriteInclusiveKm = ref(!!formValues.customInclusiveKm);
const unchangedValues = ref(cloneDeep(formValues));

const onSubmit = handleSubmit(async (values) => {
  const { status } = await confirmAndSave({
    booking: props.booking,
    updateBookingDto: {
      pricing: {
        ...values,
        priceRecalculationMode: PriceRecalculationMode.USE_EXISTING,
      },
    },
  });
  if (status !== 'saved') return;
  unchangedValues.value = cloneDeep(formValues);
});

const hasChanged = useFormHasChanged(unchangedValues, formValues);
watch(hasChanged, () => emit('hasChanged', hasChanged.value));
</script>

<i18n lang="json">
{
  "en": {
    "rentalDuration": "Rental Duration",
    "days": "Days",
    "priceCalculatedForXDays": "(Price calculated for {days} days)",
    "information": "Extra KM and Coupon KM already included!",
    "overwriteInclusiveKm": "Overwrite inclusive KM",
    "useInclusiveKmOfCar": "Use inclusive KM of Car",
    "extraKm": "Extra KM",
    "couponKm": "Coupon KM"
  },
  "de": {
    "rentalDuration": "Mietdauer",
    "days": "Tage",
    "priceCalculatedForXDays": "(Preis berechnet für {days} Tage)",
    "information": "Extra-KM und Gutschein-KM mit inbegriffen!",
    "overwriteInclusiveKm": "Inklusiv KM überschreiben",
    "useInclusiveKmOfCar": "Inklusiv KM des Autos nutzen",
    "extraKm": "Extra KM",
    "couponKm": "Coupon-KM"
  },
  "es": {
    "rentalDuration": "Duración del alquiler",
    "days": "Días",
    "priceCalculatedForXDays": "(Precio calculado para {days} días)",
    "information": "¡Extra KM y Coupon KM ya incluidos!",
    "overwriteInclusiveKm": "Sobrescribir km incluidos",
    "useInclusiveKmOfCar": "Usar km incluidos del vehículo",
    "extraKm": "Km adicionales",
    "couponKm": "Km de cupon"
  }
}
</i18n>
